import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  StarIcon as StarSolidIcon, 
  ArrowDownTrayIcon,
  LinkIcon,
  CodeBracketIcon,
  DocumentTextIcon,
  WrenchScrewdriverIcon,
  CubeTransparentIcon,
  PuzzlePieceIcon,
  PlayIcon,
  EyeIcon,
  ClockIcon,
  TagIcon,
  UserIcon,
  CalendarIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  LightBulbIcon,
  CogIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';
import { useStarterTemplates } from '../../contexts/StarterTemplateContext';
import { StarterTemplate } from '../../firebase/starterTemplateService';
import { Link, useSearchParams } from 'react-router-dom';
import { StarterTemplatesPageSkeleton } from '../../components/skeletons/PageSkeletons';

const StarterTemplatesPage: React.FC = () => {
  const { starterTemplates, featuredStarterTemplates, loading, error } = useStarterTemplates();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'downloads' | 'stars' | 'date'>('downloads');
  const [selectedTemplate, setSelectedTemplate] = useState<StarterTemplate | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Open a template's details when linked as /starter-templates?item=<id>
  useEffect(() => {
    const itemId = searchParams.get('item');
    if (!itemId || starterTemplates.length === 0) return;
    const template = starterTemplates.find((t) => t.id === itemId);
    if (template) setSelectedTemplate(template);
    setSearchParams({}, { replace: true });
  }, [searchParams, starterTemplates, setSearchParams]);

  const typeOptions = [
    { value: 'all', label: 'All Types', icon: CogIcon, color: 'bg-gray-100 text-gray-800' },
    { value: 'template', label: 'Templates', icon: DocumentTextIcon, color: 'bg-blue-100 text-blue-800' },
    { value: 'extension', label: 'Extensions', icon: PuzzlePieceIcon, color: 'bg-green-100 text-green-800' },
    { value: 'tool', label: 'Tools', icon: WrenchScrewdriverIcon, color: 'bg-yellow-100 text-yellow-800' },
    { value: 'boilerplate', label: 'Boilerplates', icon: CubeTransparentIcon, color: 'bg-purple-100 text-purple-800' },
  ];

  // Get unique categories
  const categories = Array.from(new Set(starterTemplates.map(template => template.category)));

  const filteredTemplates = starterTemplates
    .filter(template => {
      const searchMatch = searchTerm === '' || 
        template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())) ||
        template.technologies.some(tech => tech.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const typeMatch = selectedType === 'all' || template.type === selectedType;
      const categoryMatch = selectedCategory === 'all' || template.category === selectedCategory;
      
      return searchMatch && typeMatch && categoryMatch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.title.localeCompare(b.title);
        case 'downloads':
          return (b.downloads || 0) - (a.downloads || 0);
        case 'stars':
          return (b.stars || 0) - (a.stars || 0);
        case 'date':
          return new Date(b.lastUpdated || 0).getTime() - new Date(a.lastUpdated || 0).getTime();
        default:
          return 0;
      }
    });

  const getTypeIcon = (type: string) => {
    const typeOption = typeOptions.find(option => option.value === type);
    return typeOption ? typeOption.icon : DocumentTextIcon;
  };

  const getTypeColor = (type: string) => {
    const typeOption = typeOptions.find(option => option.value === type);
    return typeOption ? typeOption.color : 'bg-gray-100 text-gray-800';
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  if (loading) {
    return <StarterTemplatesPageSkeleton />;
  }

  if (error || starterTemplates.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 dark:from-gray-900 dark:via-purple-900 dark:to-indigo-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:pt-[var(--page-top)]">
          <div className="text-center">
            <div className="mx-auto h-32 w-32 text-purple-400 mb-8">
              <WrenchScrewdriverIcon className="h-full w-full" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Development Tools Coming Soon!
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
              I'm building powerful starter templates, extensions, and development tools to supercharge your workflow. 
              Amazing productivity boosters are on the way!
            </p>
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-300 mb-2">
                🛠️ What's in Development
              </h3>
              <ul className="text-left text-purple-800 dark:text-purple-400 space-y-2">
                <li>• Flutter app boilerplates</li>
                <li>• VS Code extensions for productivity</li>
                <li>• React starter templates</li>
                <li>• CLI tools and automation scripts</li>
              </ul>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://github.com/Haraprosad"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 transition-colors"
              >
                <WrenchScrewdriverIcon className="h-5 w-5 mr-2" />
                Star on GitHub
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-3 border border-gray-300 dark:border-gray-600 text-base font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Request a Tool
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-red-50 dark:from-gray-900 dark:via-purple-900 dark:to-indigo-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:pt-[var(--page-top)]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Developer
              <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                {" "}Tools & Templates
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-purple-100 mb-8 max-w-3xl mx-auto">
              Boost your development speed with my collection of starter templates, tools, and extensions
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-purple-100">
              <div className="flex items-center space-x-2">
                <WrenchScrewdriverIcon className="h-6 w-6" />
                <span className="text-lg font-medium">{starterTemplates.length} Tools & Templates</span>
              </div>
              <div className="flex items-center space-x-2">
                <ArrowDownTrayIcon className="h-6 w-6" />
                <span className="text-lg font-medium">
                  {formatNumber(starterTemplates.reduce((total, template) => total + (template.downloads || 0), 0))} Downloads
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <StarSolidIcon className="h-6 w-6" />
                <span className="text-lg font-medium">
                  {starterTemplates.reduce((total, template) => total + (template.stars || 0), 0)} Stars
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Featured Templates */}
      {featuredStarterTemplates.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                ⭐ Featured Tools & Templates
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300">
                My most popular and useful development resources
              </p>
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {featuredStarterTemplates.slice(0, 6).map((template) => {
                const TypeIcon = getTypeIcon(template.type);
                
                return (
                  <motion.div
                    key={template.id}
                    variants={itemVariants}
                    whileHover={{ y: -10, scale: 1.02 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-200 dark:border-gray-700 cursor-pointer"
                    onClick={() => setSelectedTemplate(template)}
                  >
                    {/* Template Header */}
                    <div className="p-6">
                      <div className="flex items-center space-x-4 mb-4">
                        {template.coverImage ? (
                          <img
                            src={template.coverImage}
                            alt={template.title}
                            className="w-16 h-16 rounded-xl object-cover"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                            <TypeIcon className="h-8 w-8 text-white" />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white line-clamp-1">
                              {template.title}
                            </h3>
                            <StarSolidIcon className="h-5 w-5 text-yellow-400" />
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(template.type)}`}>
                              {template.type}
                            </span>
                            {template.version && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                v{template.version}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                        {template.description}
                      </p>

                      {/* Technology Stack */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {template.technologies.slice(0, 3).map((tech) => (
                          <span
                            key={tech}
                            className="inline-flex items-center px-2 py-1 rounded-md bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 text-xs font-medium"
                          >
                            {tech}
                          </span>
                        ))}
                        {template.technologies.length > 3 && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-medium">
                            +{template.technologies.length - 3} more
                          </span>
                        )}
                      </div>

                      {/* Stats */}
                      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-1">
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            <span>{formatNumber(template.downloads || 0)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <StarSolidIcon className="h-4 w-4" />
                            <span>{template.stars || 0}</span>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400">
                          {template.category}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>
      )}

      {/* All Templates Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Complete Collection
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Browse all development tools, templates, and resources
            </p>
          </motion.div>

          {/* Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 mb-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Search
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Search templates..."
                  />
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                </div>
              </div>

              {/* Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Type
                </label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="block w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                >
                  {typeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="block w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="all">All Categories</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sort */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sort By
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="block w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="downloads">Most Downloaded</option>
                  <option value="stars">Most Starred</option>
                  <option value="date">Latest</option>
                  <option value="name">Name A-Z</option>
                </select>
              </div>
            </div>
          </motion.div>

          {/* Templates Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filteredTemplates.map((template) => {
              const TypeIcon = getTypeIcon(template.type);
              
              return (
                <motion.div
                  key={template.id}
                  variants={itemVariants}
                  whileHover={{ y: -5, scale: 1.02 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 dark:border-gray-700 cursor-pointer"
                  onClick={() => setSelectedTemplate(template)}
                >
                  <div className="p-6">
                    {/* Template Header */}
                    <div className="flex items-center space-x-4 mb-4">
                      {template.coverImage ? (
                        <img
                          src={template.coverImage}
                          alt={template.title}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                          <TypeIcon className="h-6 w-6 text-white" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white line-clamp-1">
                            {template.title}
                          </h3>
                          {template.featured && <StarSolidIcon className="h-5 w-5 text-yellow-400" />}
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {template.category} • {template.type}
                        </p>
                      </div>
                    </div>

                    <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                      {template.description}
                    </p>

                    {/* Technologies */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {template.technologies.slice(0, 3).map((tech) => (
                        <span
                          key={tech}
                          className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-medium"
                        >
                          {tech}
                        </span>
                      ))}
                      {template.technologies.length > 3 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs">
                          +{template.technologies.length - 3}
                        </span>
                      )}
                    </div>

                    {/* Stats */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                                              <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex items-center space-x-1">
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            <span>{formatNumber(template.downloads || 0)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <StarSolidIcon className="h-4 w-4" />
                            <span>{template.stars || 0}</span>
                          </div>
                        </div>
                      <div className="flex items-center space-x-2">
                        {template.githubUrl && (
                          <a
                            href={template.githubUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                          >
                            <CodeBracketIcon className="h-5 w-5" />
                          </a>
                        )}
                        {template.demoUrl && (
                          <a
                            href={template.demoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 transition-colors"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          {filteredTemplates.length === 0 && (
            <div className="text-center py-12">
              <WrenchScrewdriverIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No templates found
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                Try adjusting your search terms or filters
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Template Detail Modal */}
      <AnimatePresence mode="wait">
        {selectedTemplate && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 overflow-y-auto"
          >
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75"
                onClick={() => setSelectedTemplate(null)}
              />

              <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 30, scale: 0.95 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full sm:p-6 max-h-[90vh] overflow-y-auto relative"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="bg-white dark:bg-gray-800 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    onClick={() => setSelectedTemplate(null)}
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="w-full">
                  {/* Template Header */}
                  <div className="flex items-center space-x-6 mb-6">
                    {selectedTemplate.coverImage ? (
                      <img
                        src={selectedTemplate.coverImage}
                        alt={selectedTemplate.title}
                        className="w-20 h-20 rounded-xl object-cover"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                        {React.createElement(getTypeIcon(selectedTemplate.type), { className: "h-10 w-10 text-white" })}
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
                          {selectedTemplate.title}
                        </h3>
                        {selectedTemplate.featured && <StarSolidIcon className="h-8 w-8 text-yellow-400" />}
                      </div>
                      <div className="flex items-center space-x-4 mt-2">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getTypeColor(selectedTemplate.type)}`}>
                          {selectedTemplate.type}
                        </span>
                        <span className="text-lg text-gray-600 dark:text-gray-400">
                          {selectedTemplate.category}
                        </span>
                        {selectedTemplate.version && (
                          <span className="text-lg text-gray-600 dark:text-gray-400">
                            v{selectedTemplate.version}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-6 mt-2 text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center space-x-1">
                          <ArrowDownTrayIcon className="h-5 w-5" />
                          <span>{formatNumber(selectedTemplate.downloads || 0)} downloads</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <StarSolidIcon className="h-5 w-5" />
                          <span>{selectedTemplate.stars || 0} stars</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <UserIcon className="h-5 w-5" />
                          <span>by {selectedTemplate.author}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex flex-wrap gap-4 mb-6">
                    {selectedTemplate.githubUrl && (
                      <a
                        href={selectedTemplate.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-purple-600 hover:bg-purple-700 transition-colors"
                      >
                        <CodeBracketIcon className="h-5 w-5 mr-2" />
                        View Source
                      </a>
                    )}
                    {selectedTemplate.downloadUrl && (
                      <a
                        href={selectedTemplate.downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-6 py-3 border border-gray-300 dark:border-gray-600 text-base font-medium rounded-xl text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                        Download
                      </a>
                    )}
                    {selectedTemplate.demoUrl && (
                      <a
                        href={selectedTemplate.demoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-6 py-3 border border-gray-300 dark:border-gray-600 text-base font-medium rounded-xl text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                      >
                        <EyeIcon className="h-5 w-5 mr-2" />
                        Live Demo
                      </a>
                    )}
                    {selectedTemplate.documentationUrl && (
                      <a
                        href={selectedTemplate.documentationUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-6 py-3 border border-gray-300 dark:border-gray-600 text-base font-medium rounded-xl text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                      >
                        <DocumentTextIcon className="h-5 w-5 mr-2" />
                        Documentation
                      </a>
                    )}
                  </div>

                  {/* Description */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      📖 Description
                    </h4>
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                      {selectedTemplate.longDescription}
                    </p>
                  </div>

                  {/* Grid Layout for Details */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Left Column */}
                    <div className="space-y-6">
                      {/* Features */}
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                          ✨ Key Features
                        </h4>
                        <ul className="space-y-2">
                          {selectedTemplate.features.map((feature, index) => (
                            <li key={index} className="flex items-start space-x-2">
                              <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-600 dark:text-gray-300">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Benefits */}
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                          💡 Benefits
                        </h4>
                        <ul className="space-y-2">
                          {selectedTemplate.benefits.map((benefit, index) => (
                            <li key={index} className="flex items-start space-x-2">
                              <LightBulbIcon className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-600 dark:text-gray-300">{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Use Cases */}
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                          🎯 Use Cases
                        </h4>
                        <ul className="space-y-2">
                          {selectedTemplate.useCases.map((useCase, index) => (
                            <li key={index} className="flex items-start space-x-2">
                              <RocketLaunchIcon className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-600 dark:text-gray-300">{useCase}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-6">
                      {/* Technologies */}
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                          🛠️ Technologies
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedTemplate.technologies.map((tech) => (
                            <span
                              key={tech}
                              className="inline-flex items-center px-3 py-1 rounded-full bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 text-sm font-medium"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Requirements */}
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                          📋 Requirements
                        </h4>
                        <ul className="space-y-2">
                          {selectedTemplate.requirements.map((req, index) => (
                            <li key={index} className="flex items-start space-x-2">
                              <CogIcon className="h-5 w-5 text-gray-500 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-600 dark:text-gray-300">{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Tags */}
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                          🏷️ Tags
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedTemplate.tags.map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm"
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Installation Instructions */}
                  <div className="mt-8">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      🚀 Installation
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4">
                      <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                        {selectedTemplate.installation}
                      </pre>
                    </div>
                  </div>

                  {/* Usage Instructions */}
                  <div className="mt-6">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      📚 Usage
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4">
                      <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                        {selectedTemplate.usage}
                      </pre>
                    </div>
                  </div>

                  {/* Screenshots */}
                  {selectedTemplate.screenshots && selectedTemplate.screenshots.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                        📱 Screenshots
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {selectedTemplate.screenshots.map((screenshot, index) => (
                          <img
                            key={index}
                            src={screenshot}
                            alt={`Screenshot ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Technical Details */}
                  <div className="mt-6 text-sm text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="font-medium">License:</span> {selectedTemplate.license}
                      </div>
                      {selectedTemplate.version && (
                        <div>
                          <span className="font-medium">Version:</span> {selectedTemplate.version}
                        </div>
                      )}
                      {selectedTemplate.lastUpdated && (
                        <div>
                          <span className="font-medium">Last Updated:</span> {new Date(selectedTemplate.lastUpdated).toLocaleDateString()}
                        </div>
                      )}
                      {selectedTemplate.contributors && selectedTemplate.contributors.length > 0 && (
                        <div>
                          <span className="font-medium">Contributors:</span> {selectedTemplate.contributors.join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StarterTemplatesPage; 